import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import ArchiveIcon from '@mui/icons-material/Archive';
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material'
import Button from '@mui/material/Button'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'


export const ManagePageForm = ({
    pageId
}: {
    pageId: string
    handleClose: () => void
}) => {
    const faberToken = OrbitSession.get('faberToken')
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [selectedItemIdToArchive, setSelectedItemIdToArchive] = useState<string | null>(null);

    interface GridRowDataType {
        id: string;
        name: string;
        dataInGB: number;
        sms: number;
        validityInDays: number;
        voiceInMins: number;
    }

    const { mutate: archiveCatalogItem, isLoading: isArchiving } = useMutation(
        (id: string) => CatalogAPI.archiveCatalogFormPage(faberToken, id),
        {
            onSuccess: () => {
                toast.success('Catalog item archived successfully!');
                setOpenConfirmDialog(false);
            },
            onError: (error: unknown) => {
                toast.error(`Failed to archive catalog item: ${error || 'An error occurred'}`);
                setOpenConfirmDialog(false);
            }
        }
    );

    const { data: catalogItems } = useQuery(
        [`getAllCatalogItemsById_${pageId}`],
        async () => CatalogAPI.getAllCatalogItemsById(faberToken, pageId)
    );

    const [catalogRows, setCatalogRows] = useState<GridRowDataType[]>([]);

    useEffect(() => {
        if (catalogItems && catalogItems.data) {
            setCatalogRows(catalogItems.data);
        }
    }, [catalogItems]);

    const handleOpenConfirmDialog = (id: string) => {
        setSelectedItemIdToArchive(id);
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setSelectedItemIdToArchive(null);
    };

    const handleConfirmArchive = () => {
        if (selectedItemIdToArchive) {
            archiveCatalogItem(selectedItemIdToArchive);
        }
    };

    const catalogColumns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            filterable: true,
            flex: 0.95,
        },
        { field: "dataInGB", headerName: "Data In GB", flex: .50 },
        { field: "validityInDays", headerName: "Validity In Days", flex: 0.50 },
        {
            field: "sms",
            headerName: "SMS",
            filterable: true,
            flex: 0.50
        },
        {
            field: "voiceInMins",
            headerName: "VoiceInMins",
            filterable: true,
            flex: 0.50
        },
        {
            field: "updatePage",
            flex: 0.50,
            filterable: false,
            sortable: false,
            headerName: "Archive",
            renderCell: (params: { row }) => {
                const { row } = params;
                return (
                    <Button
                        variant='contained'
                        onClick={() => handleOpenConfirmDialog(row.page_fk_id)}
                        disabled={isArchiving}
                    >
                        <ArchiveIcon />
                    </Button>
                );
            },
        },
    ];

    /**
     * Event handlers
     */
    return (
        <Box>
            <Grid item xs={3}>
                <NormalText isBold>Underlying Package</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
                <DataGrid
                    rows={catalogRows}
                    columns={catalogColumns}
                    rowsPerPageOptions={[10]}
                    pageSize={10}
                    getRowId={(row) => row.id}
                />
            </Grid>
            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className=''
            >
                <DialogTitle id="alert-dialog-title" >
                    {"Confirm Archive"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description align-center" >
                        Are you sure you want to archive this catalog item? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog} color="primary" disabled={isArchiving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmArchive}
                        autoFocus
                        variant="contained"
                        disabled={isArchiving}
                    >
                        {isArchiving ? 'Archiving...' : 'Archive'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
